<script setup lang="ts">
import { FooterProps } from "./TheFooter.props"
import { safeWebsiteIconsSrcMap } from "./TheFooter.iconMapping"
import TheFooterBottom from "./TheFooterBottom.server.vue"
import { customPaths } from "~/utils/constants"
import { helpSubjectParams } from "~/utils/contactFormPaths"

withDefaults(defineProps<FooterProps>(), {})

const returnsContactPath = `${customPaths.contactUs}?help_subject=${helpSubjectParams.returns}`
const { sendEventPhoneHelp } = useGA4SiteEvents()
const { height: windowHeight } = useWindowSize()
const pageHeight = useState<number>("wrapper-page-content-height-throttled")

const isLongPage = computed<boolean>(
  () => pageHeight.value > 3 * windowHeight.value
)
</script>

<template>
  <footer class="footer-wrapper grid-container text-white z-20">
    <div
      v-if="isLongPage"
      class="
        back-to-top-container
        bg-tiffany-main
        col-span-full
        py-2
        text-center
      "
      @click="useScrollTop"
    >
      <UtilsButton
        class="custom-btn-back-to-top-button"
        theme="link-white"
        :text="$t('footer.textBackToTop')"
        order="last"
      >
        <template #icon>
          <UtilsIcon
            decoding="async"
            fetch-priority="low"
            name="ArrowUp.svg"
            color="white"
            class="h-5 w-5"
          />
        </template>
      </UtilsButton>
    </div>

    <div
      class="
        top-footer
        md:grid-container
        padded
        bg-slate-main
        col-span-full
        py-6
        md:py-10
      "
    >
      <div class="accordion-container flex flex-col gap-2 md:hidden">
        <!-- Accordion Item  -->
        <AppAccordion
          v-for="(menuItem, index_list) in linkList"
          :key="`accordion_menu_${index_list}`"
          class="custom-accordion-footer beaver-bold py-2"
          :text="menuItem.title"
          with-icons
          :is-open="null"
        >
          <template #icon-to-open>
            <UtilsIcon
              decoding="async"
              fetch-priority="low"
              name="ChevronDown.svg"
              color="white"
              class="h-6 w-6"
            />
          </template>

          <template #icon-to-close>
            <UtilsIcon
              decoding="async"
              fetch-priority="low"
              name="ChevronUp.svg"
              color="white"
              class="h-6 w-6"
            />
          </template>
          <template #body>
            <div class="my-2 flex flex-col items-start gap-4">
              <UtilsButton
                v-for="(listItem, index) in menuItem.list"
                :key="`accordio_link${index}`"
                class="custom-btn-mouse [&>span]:p-0 [&>span]:text-left"
                :text="listItem.label"
                :path="listItem.path"
                theme="ghost-white"
              />
              <template v-if="index_list === 2">
                <a
                  class="
                    mouse
                    custom-btn-mouse
                    btn-link-white
                    whitespace-normal
                    p-0
                    text-left
                  "
                  :href="`javascript: Cookiebot.renew()`"
                  >{{ $t("footer.cookieSetting") }}</a
                >
                <UtilsButton
                  class="custom-btn-mouse-medium"
                  custom-classes="!px-4 !py-3"
                  :text="$t('footer.withdrawFromContract')"
                  :path="returnsContactPath"
                  theme="contained-white"
                />
              </template>
            </div>
          </template>
        </AppAccordion>
      </div>

      <!-- Desktop -->
      <div
        v-if="linkList?.length"
        class="col-span-8 hidden grid-cols-8 gap-6 md:grid"
      >
        <!-- First 3 columns -->
        <div class="col-span-6 grid grid-cols-6 gap-6">
          <div
            v-for="(link, index_list) in linkList.slice(0, 3)"
            :key="link.title"
            class="col-span-2"
            style="flex: 0 0 25%"
          >
            <p class="pig-bold mb-4">
              {{ link.title }}
            </p>
            <div class="flex flex-col items-start gap-4">
              <UtilsButton
                v-for="(listItem, index) in link.list"
                :key="`menuTopFarmacia_link${index}`"
                class="
                  custom-btn-mouse
                  w-full
                  [&>span>span]:w-full
                  [&>span>span]:whitespace-normal
                  [&>span]:w-full
                  [&>span]:whitespace-normal
                  [&>span]:p-0
                  [&>span]:text-left
                "
                :text="listItem.label"
                theme="ghost-white"
                :to="listItem.path"
              />
              <template v-if="index_list === 2">
                <a
                  class="
                    mouse
                    custom-btn-mouse
                    btn-link-white
                    whitespace-normal
                    p-0
                    text-left
                  "
                  :href="`javascript: Cookiebot.renew()`"
                  >{{ $t("footer.cookieSetting") }}</a
                >
                <UtilsButton
                  class="custom-btn-mouse-medium"
                  custom-classes="!px-4 !py-3"
                  :text="$t('footer.withdrawFromContract')"
                  :path="returnsContactPath"
                  theme="contained-white"
                />
              </template>
            </div>
          </div>
        </div>
        <!-- Fourth column -->
        <div class="col-span-2 flex flex-col gap-8">
          <div v-for="link in linkList.slice(3)" :key="link.title">
            <p class="pig-bold mb-4">
              {{ link.title }}
            </p>
            <div class="flex flex-col items-start gap-4">
              <UtilsButton
                v-for="(listItem, index) in link.list"
                :key="`menuTopFarmacia_link${index}`"
                class="
                  custom-btn-mouse
                  [&>span]:whitespace-normal
                  [&>span]:p-0
                  [&>span]:text-left
                "
                :text="listItem.label"
                theme="ghost-white"
                :to="listItem.path"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Fifth column -->
      <div
        class="
          adress-and-contact-container
          mouse-medium
          mt-6
          gap-4
          md:col-span-2
          md:mt-0
        "
        id="address-and-contact-container"
      >
        <div class="flex items-center gap-4 md:flex-col md:items-start">
          <p class="beaver-bold md:pig-bold block no-underline">
            {{ $t("footer.followUs") }}
          </p>

          <!-- Social -->
          <div
            class="
              flex flex-wrap
              items-center
              gap-5
              md:flex-col
              md:items-start
              md:gap-2
            "
          >
            <div class="flex flex-wrap gap-4">
              <AppLink to="https://www.facebook.com/TopFarmacia/">
                <UtilsIcon
                  deconding="async"
                  fetch-priority="low"
                  name="FacebookRounded.svg"
                  class="cursor-pointer"
                />
              </AppLink>

              <AppLink to="https://www.instagram.com/top_farmacia">
                <UtilsIcon
                  deconding="async"
                  fetch-priority="low"
                  name="InstagramRounded.svg"
                  class="cursor-pointer"
                />
              </AppLink>
            </div>
          </div>
        </div>
      </div>

      <div class="safe-website-container mt-4 md:col-span-2 md:mt-0">
        <p class="beaver-bold md:pig-bold mb-2">
          {{ $t("footer.safeWebsite") }}
        </p>

        <div class="mb-6 flex flex-wrap gap-2">
          <AppLink
            v-for="icon in safeWebsiteIconsList"
            :key="`safeWebsite-${icon.name}`"
            class="mb-2 inline-block h-10 cursor-pointer"
            :to="icon.path"
          >
            <UtilsIcon
              v-bind="safeWebsiteIconsSrcMap[icon.name]"
              class="h-[60px] w-auto"
            />
          </AppLink>
        </div>

        <div>
          <p class="snail mb-4">
            {{ $t("footer.verifyOurEcommerce") }}
          </p>

          <div
            class="
              flex
              h-fit
              flex-wrap
              items-center
              gap-2
              md:flex-nowrap
              md:justify-between
            "
          >
            <AppLink
              v-if="healthyMinisterIcon"
              :key="`healthyMinister-${healthyMinisterIcon.name}`"
              class="flex cursor-pointer items-center"
              :to="healthyMinisterIcon.path"
            >
              <UtilsIcon
                v-bind="safeWebsiteIconsSrcMap[healthyMinisterIcon.name]"
                class="!h-[60px] md:!min-h-[96px]"
              />
            </AppLink>
            <CmsComponentMinisterialBadges
              v-if="MinisterialBadgesList"
              :Badges="MinisterialBadgesList"
              classImage="!h-[60px] md:!min-h-[96px]"
            />
          </div>
        </div>
      </div>
    </div>
    <TheFooterBottom v-if="footerIconsList" v-bind="footerIconsList" />
  </footer>
</template>
